/**
 * Suggested-terms engine (Task 1.6 / TECHNICAL §8b).
 *
 * Produces a deterministic starting point for `target_discount_pct`,
 * `target_days_early`, `floor_discount_pct`, and `floor_apr` that pre-fills the
 * `NewNegotiationDialog`.  The operator is always free to override.
 *
 * Strategy
 * ─────────
 * floor_apr        = costOfCapitalApr (the buyer's minimum acceptable yield)
 * target_days_early = inferred from the vendor's historical invoice terms
 *                     (how many days early they've typically been paid / net days)
 * target_discount_pct = discountToApr inverse at target_days_early, anchored
 *                       slightly above the vendor's best historical discount so
 *                       we ask for a *better* deal than what was informally captured.
 * floor_discount_pct  = aprToDiscount(costOfCapitalApr, target_days_early)
 *
 * Fallbacks when history is sparse / absent:
 *  - No discount history → start from an ask of costOfCapitalApr * 1.25 (25 % above floor)
 *  - No avg_days_paid_early → fall back to 20 days early (common SCF term)
 *  - Clamped: target ≥ floor + 0.01; floor ≥ 0.01; days ≥ 1
 */

import type { VendorSummary, PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import { discountToApr, aprToDiscount } from "./terms";

export interface SuggestedTerms {
  /** Discount % to open with in the negotiation. */
  target_discount_pct: number;
  /** Number of days early (before net due date) the target discount applies to. */
  target_days_early: number;
  /** Minimum discount % the buyer will accept at target_days_early. */
  floor_discount_pct: number;
  /** Minimum effective APR the buyer will accept (= costOfCapitalApr). */
  floor_apr: number;
}

/** Minimum sensible days-early when no history is available. */
const DEFAULT_DAYS_EARLY = 20;

/** Minimum discount in percentage points we'll ever suggest. */
const MIN_DISCOUNT_PCT = 0.01;

/** Multiplier above costOfCapitalApr used when there is no discount history. */
const TARGET_APR_MULTIPLIER_NO_HISTORY = 1.25;

/** Multiplier applied on top of the best historical APR to set the opening ask. */
const TARGET_APR_MULTIPLIER_WITH_HISTORY = 1.15;

/**
 * Round a number to a given number of decimal places.
 */
function round(value: number, decimals = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Infer typical days-early from the vendor's invoice rows.
 *
 * Uses the median of positive days_paid_early values (actual early payments)
 * to avoid being skewed by outliers.  Falls back to avg_days_paid_early from
 * the summary, then to DEFAULT_DAYS_EARLY.
 */
function inferDaysEarly(summary: VendorSummary, rows: PARInvoiceRow[]): number {
  const earlyDays = rows
    .filter((r) => r.vendor_name === summary.vendor_name && r.days_paid_early > 0)
    .map((r) => r.days_paid_early)
    .sort((a, b) => a - b);

  if (earlyDays.length > 0) {
    const mid = Math.floor(earlyDays.length / 2);
    const median =
      earlyDays.length % 2 === 1 ? earlyDays[mid] : (earlyDays[mid - 1] + earlyDays[mid]) / 2;
    return Math.max(1, Math.round(median));
  }

  if (summary.avg_days_paid_early > 0) {
    return Math.max(1, Math.round(summary.avg_days_paid_early));
  }

  return DEFAULT_DAYS_EARLY;
}

/**
 * Suggest negotiation target and floor terms for a vendor.
 *
 * @param vendorSummary   Aggregated stats for the vendor (from csvInvoiceParser).
 * @param rows            All invoice rows (used to derive richer day-early stats).
 * @param costOfCapitalApr  The buyer's cost of capital (e.g. 12 for 12 %).
 *                          This becomes floor_apr.
 * @returns A `SuggestedTerms` object ready to pre-fill the negotiation dialog.
 */
export function suggestTerms(
  vendorSummary: VendorSummary,
  rows: PARInvoiceRow[],
  costOfCapitalApr: number
): SuggestedTerms {
  // --- Floor ---
  const floorApr = costOfCapitalApr > 0 ? costOfCapitalApr : 0;

  // --- Days early ---
  const daysEarly = inferDaysEarly(vendorSummary, rows);

  // Round computed discount % to whole percentage points (operators think in
  // round numbers — 1%/2%, not 0.83%). Floor is at least 1% so suggestions
  // never come out below the round-number minimum operators expect.
  const roundWhole = (v: number) => Math.max(1, Math.round(v));

  // --- Floor discount at the inferred days ---
  const rawFloorDiscount = aprToDiscount(floorApr, daysEarly);
  const floorDiscountPct = roundWhole(Math.max(MIN_DISCOUNT_PCT, rawFloorDiscount));

  // --- Target discount ---
  let targetDiscountPct: number;

  if (vendorSummary.best_discount_pct != null && vendorSummary.best_discount_pct > 0) {
    // We have historical discount data — push the opening ask above the best
    // historically captured rate, expressed as APR, then convert back.
    const bestApr = discountToApr(vendorSummary.best_discount_pct, daysEarly);
    // Stretch above best historical; also stay above floor
    const targetApr = Math.max(bestApr * TARGET_APR_MULTIPLIER_WITH_HISTORY, floorApr * 1.1);
    targetDiscountPct = roundWhole(aprToDiscount(targetApr, daysEarly));
  } else {
    // Sparse / no discount history — open at 25 % above the floor APR
    const targetApr = floorApr * TARGET_APR_MULTIPLIER_NO_HISTORY;
    targetDiscountPct = roundWhole(aprToDiscount(targetApr, daysEarly));
  }

  // Target must be strictly above floor (next whole percent up).
  if (targetDiscountPct <= floorDiscountPct) {
    targetDiscountPct = floorDiscountPct + 1;
  }

  return {
    target_discount_pct: targetDiscountPct,
    target_days_early: daysEarly,
    floor_discount_pct: floorDiscountPct,
    floor_apr: floorApr,
  };
}
